import type { WorkflowPurpose, WorkflowSummary } from "@closer/shared";
import {
  WORKFLOW_DEFINITION_STATUS_NAMES,
  WORKFLOW_PURPOSE_NAMES,
  WORKFLOW_PURPOSES,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * İŞ AKIŞLARI (Dinamik Workflow — Kart 2/3).
 *
 * ### ⚠ Sayfa doğrudan BOŞ CANVAS ile açılmaz
 *
 * UI/UX §6: ilk yüzey, yeniden kullanılabilir akışların yönetildiği bir listedir. Builder'a
 * doğrudan düşseydi kullanıcı hangi akışı düzenlediğini bilmeden çizim yapardı.
 *
 * ### ⚠ Taslak ve yayınlanmış sürüm AYRI gösteriliyor
 *
 * Aynı akışın yayında bir sürümü ve üzerinde çalışılan bir taslağı aynı anda olabilir
 * (§6.4). Tek "sürüm" alanı gösterseydik kullanıcı hangisinin canlı olduğunu bilemezdi.
 *
 * ### ⚠ Bu ekran YETKİ VERMİYOR
 *
 * UI/UX §5.4: İş Akışları ekranı permission verme/çıkarma yüzeyine dönüşmez; `workflow.*`
 * yetkileri `Roller ve Yetkiler` sayfasından rol permission'ı olarak yönetilir. Bir akış
 * tanımlamak, orada yer alan kullanıcı ya da role hiçbir yetki kazandırmaz.
 *
 * ### ⚠ Aşama düzenleme bu dilimde YOK
 *
 * Görsel builder kendi kartının işi (ADR-0014). Buraya "şimdilik basit bir aşama formu"
 * koymak, sonra atılacak ikinci bir düzenleme yüzeyi üretirdi.
 */
export function WorkflowsPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [ad, setAd] = useState("");
  const [aciklama, setAciklama] = useState("");
  /** ⚠ Varsayılan YOK: amaç kasıtlı seçilmeli (CLOS-1213). */
  const [amac, setAmac] = useState<WorkflowPurpose | "">("");
  const [formAcik, setFormAcik] = useState(false);

  const yonetebilir = can("workflow.manage");
  const yayinlayabilir = can("workflow.publish");

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "workflows"),
    queryFn: async () => (await api<WorkflowSummary[]>("/api/workflows", { tenantId })).data,
  });

  /**
   * ⚠ VARSAYILAN ATAMA `workflow.publish` KAPISINDA (CLOS-1201).
   *
   * Bir akışı varsayılan yapmak, bundan sonraki BÜTÜN sözleşmelerin hangi süreçten
   * geçeceğini belirliyor — taslak düzenlemekle aynı ağırlıkta değil. Yetki kataloğu da onu
   * "yayınlama ve varsayılan atama" diye tanımlıyor.
   */
  const varsayilanYap = useMutation({
    /**
     * ⚠ AMAÇ AKIŞIN KENDİSİNDEN okunuyor, kullanıcıya ayrıca sorulmuyor (CLOS-1213).
     * Sorsaydık, müzakere akışını "nihai onay varsayılanı" yapmak mümkün olurdu ve
     * kiracının nihai onayı müzakere akışıyla yürürdü.
     */
    mutationFn: async (akis: WorkflowSummary) =>
      (
        await api<{ ok: boolean }>(`/api/workflows/${akis.id}/default`, {
          method: "PUT",
          tenantId,
          body: JSON.stringify({ contractType: null, purpose: akis.purpose }),
        })
      ).data,
    /**
     * ⚠ LİSTE TAZELENİYOR (CLOS-1204). Eskiden yalnız bildirim çıkıyordu ve liste eski
     * hâliyle kalıyordu — kullanıcı kararını verdikten sonra ekranda hiçbir değişiklik
     * görmüyordu. Kusurun yarısı buydu: alan API'de yoktu, diğer yarısı sayfanın
     * yenilenmemesiydi.
     */
    onSuccess: async () => {
      toast.success("Bu akış kiracı varsayılanı oldu");
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workflows") });
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Varsayılan atanamadı"),
  });

  /**
   * VARSAYILANI KALDIR (CLOS-1204).
   *
   * ⚠ Neden gerekli: kiracı varsayılanı dışa gönderim kapısını MUTLAK hâle getiriyor
   * (CLOS-1201). Kaldırma yolu olmayınca yanlışlıkla atanan bir varsayılan geri alınamayan
   * bir kilit üretiyordu — ağırlığı olan bir kararın geri alma yolu olmalı.
   *
   * ⚠ Amaç gönderilmiyor: sunucu tanımın kendi amacından okuyor (atamayla aynı gerekçe).
   */
  const varsayilaniKaldir = useMutation({
    mutationFn: async (akis: WorkflowSummary) =>
      (
        await api<{ ok: boolean }>(`/api/workflows/${akis.id}/default`, {
          method: "DELETE",
          tenantId,
        })
      ).data,
    onSuccess: async () => {
      toast.success("Kiracı varsayılanı kaldırıldı");
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workflows") });
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Varsayılan kaldırılamadı"),
  });

  /**
   * ARŞİVLE / ARŞİVDEN ÇIKAR (CLOS-1205).
   *
   * ⚠ SİLME YOK: tarihsel referansı olan akış arşivlenir (UI/UX §7.1/§40). Silinseydi
   * geçmiş sözleşmelerin hangi süreçten geçtiği cevapsız kalırdı.
   *
   * ⚠ TEK MUTATION, İKİ UÇ: ikisini ayrı mutation'lara bölseydik `isPending` iki yerde
   * yaşar ve aynı satırdaki tek düğme hangi bekleyişte olduğunu bilemezdi.
   */
  const arsivDurumu = useMutation({
    mutationFn: async (akis: WorkflowSummary) =>
      (
        await api<{ ok: boolean }>(
          `/api/workflows/${akis.id}/${akis.status === "archived" ? "unarchive" : "archive"}`,
          { method: "POST", tenantId, body: JSON.stringify({}) },
        )
      ).data,
    onSuccess: async (_veri, akis) => {
      toast.success(
        akis.status === "archived" ? "İş akışı arşivden çıkarıldı" : "İş akışı arşivlendi",
      );
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workflows") });
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Arşiv durumu değiştirilemedi"),
  });

  const olustur = useMutation({
    mutationFn: async () =>
      (
        await api<WorkflowSummary>("/api/workflows", {
          method: "POST",
          tenantId,
          body: JSON.stringify({ name: ad, purpose: amac, description: aciklama.trim() || null }),
        })
      ).data,
    onSuccess: async (yeni) => {
      setAd("");
      setAciklama("");
      setFormAcik(false);
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workflows") });
      toast.success(`"${yeni.name}" taslak olarak oluşturuldu`);
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İş akışı oluşturulamadı"),
  });

  if (sorgu.isPending) return <Skeleton lines={5} />;
  if (sorgu.isError) {
    return <StateView state={resolveErrorState(sorgu.error, { idempotent: true })} />;
  }

  const akislar = sorgu.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="İş Akışları"
        description="Sözleşmelerin şirket içinde hangi aşamalardan geçeceğini tanımlayın."
        actions={
          yonetebilir ? (
            <Button size="sm" onClick={() => setFormAcik((a) => !a)}>
              {formAcik ? "Vazgeç" : "İş Akışı Oluştur"}
            </Button>
          ) : undefined
        }
      />

      {formAcik ? (
        <Card>
          <div className="flex flex-col gap-3">
            <FormField label="İş akışı adı">
              <Input
                id="akis-adi"
                value={ad}
                onChange={(olay) => setAd(olay.target.value)}
                placeholder="Örn. Standart Sözleşme Akışı"
              />
            </FormField>
            <FormField label="Amaç">
              {/**
               * ⚠ Boş seçenek KASITLI: amaç varsayılanı yok. Varsayılan koysaydık, amacını
               * hiç düşünmeden kurulan her akış aynı amaca düşer ve kanonun ayırdığı iki
               * karar (müzakere / nihai iç onay) kurulum anında karışırdı.
               */}
              <Select
                id="akis-amaci"
                value={amac}
                onChange={(olay) => setAmac(olay.target.value as WorkflowPurpose | "")}
              >
                <option value="">Seçiniz</option>
                {WORKFLOW_PURPOSES.map((p) => (
                  <option key={p} value={p}>
                    {WORKFLOW_PURPOSE_NAMES[p]}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Açıklama (opsiyonel)">
              <Textarea
                id="akis-aciklama"
                value={aciklama}
                onChange={(olay) => setAciklama(olay.target.value)}
                rows={2}
              />
            </FormField>
            <div>
              <Button
                size="sm"
                onClick={() => olustur.mutate()}
                loading={olustur.isPending}
                disabled={ad.trim() === "" || amac === ""}
              >
                Oluştur
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {akislar.length === 0 ? (
        <EmptyState
          title="Henüz iş akışı yok"
          description={
            yonetebilir
              ? "Sözleşmelerin şirket içi inceleme ve onay sırasını tanımlamak için bir iş akışı oluşturun."
              : "Organizasyonunuzda henüz tanımlanmış bir iş akışı bulunmuyor."
          }
        />
      ) : (
        <div className="flex flex-col gap-2">
          {akislar.map((akis) => (
            <Card key={akis.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    {/**
                     * ⚠ AD BİR BAĞLANTI: tasarımcıya giden kapı burası.
                     *
                     * Canlıda ölçüldü (10 Eylül 2026): liste akışı gösteriyor ama hiçbir
                     * yere götürmüyordu — kullanıcı akışı oluşturup orada kalıyordu.
                     * Kartın tamamını tıklanabilir yapmak yerine ADın bağlantı olması
                     * klavyeyle de gezilebilir tek doğru biçim.
                     */}
                    <Link
                      to={`/app/organizasyon/is-akislari/${akis.id}`}
                      className="font-medium text-fg-strong underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                      {akis.name}
                    </Link>
                    <Badge tone={akis.status === "published" ? "success" : "neutral"}>
                      {WORKFLOW_DEFINITION_STATUS_NAMES[akis.status]}
                    </Badge>
                    {/**
                     * ⚠ DURUM ROZETİ, KOMUTTAN AYRI (CLOS-1204 — canlıda ölçüldü).
                     *
                     * İlk yazımda varsayılan olma durumunun tek göstergesi aşağıdaki düğmenin
                     * metniydi. O düğme `workflow.publish` kapısının ardında; yani yetkisi
                     * olmayan kullanıcı hangi akışın varsayılan olduğunu HİÇ göremiyordu —
                     * kartın başlığındaki kusurun ta kendisi. Komut yetkiye bağlıdır, durum
                     * ise herkese ait bir gerçek.
                     */}
                    {akis.isTenantDefault ? <Badge tone="info">Varsayılan</Badge> : null}
                  </div>
                  {/**
                   * ⚠ Yalnız YAYINLANMIŞ akış varsayılan olabilir: taslak bir söz değildir.
                   * Sunucu da reddediyor; düğmeyi hiç çizmemek kullanıcıyı hak etmediği bir
                   * çakışma hatasına göndermiyor.
                   */}
                  {yayinlayabilir && akis.status === "published" ? (
                    <div>
                      {/*
                        ⚠ İKİ AYRI KOMUT, TEK YER (CLOS-1204). Varsayılan olan akışa ikinci
                        kez "Varsayılan yap" teklif etmek, kullanıcıya kararının işlemediğini
                        düşündürüyordu — ölçülen kusur buydu: karar veriliyor ama görünmüyor.
                      */}
                      {akis.isTenantDefault ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => varsayilaniKaldir.mutate(akis)}
                          loading={varsayilaniKaldir.isPending}
                        >
                          Varsayılanı kaldır
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => varsayilanYap.mutate(akis)}
                          loading={varsayilanYap.isPending}
                        >
                          Varsayılan yap
                        </Button>
                      )}
                    </div>
                  ) : null}
                  {/**
                   * ⚠ ARŞİV DÜĞMESİ VARSAYILAN DÜĞMESİNDEN AYRI KOŞULDA (CLOS-1205).
                   *
                   * Varsayılan yalnız YAYINLANMIŞ akışta anlamlı; arşivleme ise taslakta da
                   * gerekli — depoda silme ucu yok ve yanlış açılmış bir taslağın başka
                   * temizleme yolu bulunmuyor. Aynı koşula bağlasaydık taslak listede
                   * sonsuza dek kalırdı.
                   *
                   * ⚠ Arşivli akışta varsayılan düğmesi zaten çizilmiyor (durumu
                   * `published` değil) — sunucu da onu reddediyor, iki taraf aynı kural.
                   */}
                  {yayinlayabilir ? (
                    <div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => arsivDurumu.mutate(akis)}
                        loading={arsivDurumu.isPending}
                      >
                        {akis.status === "archived" ? "Arşivden çıkar" : "Arşivle"}
                      </Button>
                    </div>
                  ) : null}
                  {akis.description ? (
                    <p className="text-sm text-fg-soft">{akis.description}</p>
                  ) : null}
                  {/* ⚠ İki sürüm ayrı: hangisinin canlı olduğu belirsiz kalmamalı. */}
                  <p className="text-xs text-fg-soft">
                    {akis.publishedVersionNumber !== null
                      ? `Yayında: v${akis.publishedVersionNumber}`
                      : "Yayınlanmış sürüm yok"}
                    {akis.draftVersionNumber !== null
                      ? ` · Taslak: v${akis.draftVersionNumber}`
                      : ""}
                    {` · ${akis.createdByName}`}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
